import { NavLink, Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import { Container, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import JurorVoteCountBanner from '../JurorVoteCountBanner/JurorVoteCountBanner';
import styles from './JuryShell.module.scss';
import { useFeatureFlag } from '../../../shared/config';
import useJuryStore from '../../../store/juryStore';
import JuryBadge from '../JuryBadge/JuryBadge';
import CampaignLogo from '../../../components/CampaignLogo/CampaignLogo';
import CompetitionLogoImage from '../../../assets/images/logo-brand-competition.svg';
import { IconInfoCircle, IconLogout } from '@tabler/icons-react';
import { tokenStorage } from '../../../auth/tokenStorage';
import axios from '../../../axios-base';
import PageNotFoundHeader from '../../../components/Header/PageNotFoundHeader';

const JuryAuth = lazy(() => import(/* webpackChunkName: "lazy-jury" */ '../JuryAuth/JuryAuth'));
const JuryProjectsList = lazy(() => import(/* webpackChunkName: "lazy-jury" */ '../JuryProjectsList/JuryProjectsList'));
const JuryBookmarks = lazy(() => import(/* webpackChunkName: "lazy-jury" */ '../JuryBookmarks/JuryBookmarks'));
const JuryFinalVoteSubmission = lazy(
    () => import(/* webpackChunkName: "lazy-jury" */ '../JuryFinalVoteSubmission/JuryFinalVoteSubmission')
);
const JuryTermsPage = lazy(() => import(/* webpackChunkName: "lazy-jury" */ '../JuryTermsPage/JuryTermsPage'));

const routes = [
    { path: '/jury/login', component: JuryAuth },
    { path: '/jury/teilnehmer', component: JuryProjectsList },
    { path: '/jury/merkliste', component: JuryBookmarks },
    { path: '/jury/foerderung', component: JuryFinalVoteSubmission },
    { path: '/jury/rahmenbedingungen', component: JuryTermsPage },
];

const AUTH_STATES = {
    PENDING: 'pending',
    AUTHENTICATED: 'authenticated',
    UNAUTHENTICATED: 'unauthenticated',
};

const mainNavLinks = [
    { to: '/jury/teilnehmer', label: 'Teilnehmer' },
    { to: '/jury/merkliste', label: 'Merkliste', key: 'bookmarkCount' },
    { to: '/jury/foerderung', label: 'Förderung', key: 'votedCount' },
];

function JuryShell() {
    const location = useLocation();
    const history = useHistory();
    const isConfirmed = useJuryStore((s) => s.isConfirmed);
    const bookmarks = useJuryStore((s) => s.bookmarks);
    const poll = useJuryStore((s) => s.poll);
    const bookmarkCount = bookmarks.size;
    const votedCount = Object.keys(poll).length;

    useEffect(() => {
        if (isConfirmed) {
            document.body.classList.add('jury-confirmed');
        } else {
            document.body.classList.remove('jury-confirmed');
        }
        return () => {
            document.body.classList.remove('jury-confirmed');
        };
    }, [isConfirmed]);

    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const toggleLogoutModal = () => setIsLogoutModalOpen(!isLogoutModalOpen);

    const handleLogout = () => {
        axios.get('/jury/logout').then(() => {
            tokenStorage.clearAccessToken();
            setIsLogoutModalOpen(false);
            history.push('/jury/login');
        });
    };

    const [authStatus, setAuthStatus] = useState(
        tokenStorage.hasAccessToken() ? AUTH_STATES.AUTHENTICATED : AUTH_STATES.PENDING
    );

    tokenStorage.onChange = () => {
        setAuthStatus(tokenStorage.hasAccessToken() ? AUTH_STATES.AUTHENTICATED : AUTH_STATES.UNAUTHENTICATED);
    };

    useEffect(() => {
        if (authStatus !== AUTH_STATES.PENDING) return;

        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/jury/refresh');
                if (cancelled) return;
                tokenStorage.setAccessToken(res.data.accessToken);
                setAuthStatus(AUTH_STATES.AUTHENTICATED);
            } catch {
                if (cancelled) return;
                setAuthStatus(AUTH_STATES.UNAUTHENTICATED);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [authStatus]);

    if (useFeatureFlag('juryVoting') === false) return <Redirect to="/" />;

    const showNavigation = location.pathname !== '/jury/login';

    const renderNavItems = (mobile = false) => (
        <>
            {mainNavLinks.map((link) => {
                const count = link.key === 'bookmarkCount' ? bookmarkCount : votedCount;
                return (
                    <NavLink
                        key={link.to}
                        to={link.to}
                        className={`${styles.NavItem} ${mobile ? styles.MobileNavItem : ''}`}
                        activeClassName={styles.NavItemActive}
                    >
                        {link.label}
                        {link.key && count > 0 && <JuryBadge>{count}</JuryBadge>}
                    </NavLink>
                );
            })}
        </>
    );

    const LogoutButton = ({ mobile }) => (
        <button
            type="button"
            className={`${mobile ? `btn btn-link p-0 align-self-start ${styles.InfoLink}` : styles.NavItem} border-0 bg-transparent`}
            aria-label="Abmelden"
            title="Abmelden"
            onClick={(e) => {
                e.preventDefault();
                toggleLogoutModal();
            }}
        >
            <IconLogout
                size={mobile ? '2rem' : '1.6875rem'}
                strokeWidth={1.5}
                className={!mobile ? styles.InfoIcon : ''}
                aria-hidden="true"
            />
            {!mobile && 'Abmelden'}
        </button>
    );

    return (
        <Container fluid className="px-0">
            <JurorVoteCountBanner isAuthenticated={authStatus === AUTH_STATES.AUTHENTICATED} />

            {showNavigation && (
                <div className={styles.NavigationWrapper}>
                    <div className="d-lg-none px-3 py-3">
                        <div className="d-flex align-items-center justify-content-between w-100">
                            <div className={styles.Logo}>
                                <CampaignLogo src={CompetitionLogoImage} />
                            </div>
                            <div className="gap-3 d-flex">
                                <NavLink
                                    to="/jury/rahmenbedingungen"
                                    className={styles.InfoLink}
                                    activeClassName={styles.InfoLinkActive}
                                    aria-label="Rahmenbedingungen"
                                >
                                    <IconInfoCircle size="2rem" strokeWidth={1.5} />
                                </NavLink>
                                <LogoutButton mobile={true} />
                            </div>
                        </div>
                    </div>

                    <Container>
                        <Row className={`py-3 d-none d-lg-flex`}>
                            <Col className="d-flex align-items-center justify-content-between">
                                <div className={styles.Logo}>
                                    <CampaignLogo src={CompetitionLogoImage} />
                                </div>
                                <nav className={styles.NavMenu}>
                                    {renderNavItems()}
                                    <NavLink
                                        to="/jury/rahmenbedingungen"
                                        className={`${styles.NavItem}`}
                                        activeClassName={styles.NavItemActive}
                                    >
                                        <IconInfoCircle
                                            size="1.6875rem"
                                            strokeWidth={1.5}
                                            className={styles.InfoIcon}
                                            aria-hidden="true"
                                        />
                                        Rahmenbedingungen
                                    </NavLink>
                                    <LogoutButton mobile={false} />
                                </nav>
                            </Col>
                        </Row>
                    </Container>

                    <div className="d-lg-none">
                        <nav className={styles.MobileNavMenu}>{renderNavItems(true)}</nav>
                    </div>
                </div>
            )}

            <Suspense fallback={<div />}>
                <Switch>
                    <Route path="/jury/login" render={(props) => <JuryAuth {...props} />} />

                    {routes
                        .filter(({ path }) => path !== '/jury/login')
                        .map(({ path, component: Component }) => (
                            <Route
                                key={path}
                                path={path}
                                render={(props) => {
                                    if (authStatus === AUTH_STATES.PENDING) return <div />;
                                    return tokenStorage.hasAccessToken() ? (
                                        <Component {...props} />
                                    ) : (
                                        <Redirect to="/jury/login" />
                                    );
                                }}
                            />
                        ))}

                    <Route
                        path="/jury/*"
                        status={404}
                        render={() => (
                            <Container>
                                <PageNotFoundHeader />
                            </Container>
                        )}
                    />

                    {authStatus !== AUTH_STATES.PENDING && (
                        <Redirect
                            from="/jury"
                            to={tokenStorage.hasAccessToken() ? '/jury/teilnehmer' : '/jury/login'}
                            exact
                        />
                    )}
                </Switch>
            </Suspense>

            <Modal isOpen={isLogoutModalOpen} toggle={toggleLogoutModal} centered>
                <ModalHeader toggle={toggleLogoutModal}>Abmeldung bestätigen</ModalHeader>
                <ModalBody>
                    <p>Möchten Sie sich jetzt von Ihrem Konto abmelden?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={handleLogout}>
                        Bestätigen
                    </Button>
                    <Button color="primary" outline onClick={toggleLogoutModal}>
                        Abbrechen
                    </Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
}

export default JuryShell;
